import {
  Box,
  Button,
  Container,
  Grid,
  Paper,
  Typography,
  useMediaQuery,
} from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import CancelIcon from '@material-ui/icons/Cancel';
import CreditCardIcon from '@material-ui/icons/CreditCard';
import EditIcon from '@material-ui/icons/Edit';
import PhoneIcon from '@material-ui/icons/Phone';
import RedoIcon from '@material-ui/icons/Redo';
import RateReviewIcon from '@material-ui/icons/RateReview';
import {
  centsToFormattedDollars,
  longDateOnly,
  mediumDateOnly,
  personName,
  timeRange,
} from '@onwardcare/core';
import LocationRow from 'components/LocationRow';
import Map from 'components/Map';
import ParagraphText from 'components/ParagraphText';
import CancelConfirmation from 'components/rides/CancelConfirmation';
import DriverCard from 'components/rides/DriverCard';
import InProgressIndicator from 'components/rides/InProgressIndicator';
import RiderCard from 'components/rides/RiderCard';
import RideTypeBadge from 'components/rides/RideTypeBadge';
import TransportTypeBadge from 'components/rides/TransportTypeBadge';
import ZeroStateText from 'components/ZeroStateText';
import DeactivatedLabel from '../common/DeactivatedLabel';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import {
  Account,
  Ride,
  useDeclineBestTimeBidsMutationMutation,
} from '../../generated/graphql';
import { AddMedicalNecessityStatementView } from './rideFormComponents/AddMedicalNecessityStatement/AddMedicalNecessityStatementView';
import { Review } from './Review';
import { useAuth } from '../../contexts/auth-context';
import { getCmFromInches, getKgFromLBS } from './rideFormComponents/helpers';
import ENV from '../../constants/Env';
import FlashOnOutlinedIcon from '@material-ui/icons/FlashOnOutlined';
import FileCopyIcon from '@material-ui/icons/FileCopy';

import { FeedbackForm } from './FeedbackForm';
import Rating from '@material-ui/lab/Rating/Rating';
import { BestTimeDialog } from './RideDetailsComponent/BestTimeDialog';
import { RideEtaDisplay } from './RideEtaDisplay';
import Alert from '@material-ui/lab/Alert/Alert';
import { ApolloQueryResult } from '@apollo/client/core/types';
import { useEhrContext } from '../../contexts/ehr-context';
import { readIsMetric } from '../../storages/is-metric-storage';
import { useLazyQuery } from '@apollo/client';
import { loader } from 'graphql.macro';
import SplitPaymentSummary from './SplitPaymentSummary';
import { Maybe } from 'graphql/jsutils/Maybe';
import { useSessionContext } from '../../contexts/session-context';
import MncsModal from '../../mncs/MncsModal';
import { OnwardToken, getToken } from '../../auth/onward-token';
import EhrWriteRideDetails from './EhrWriteRideDetails';
import { flexibleRidePickupTimeTbd } from '../../lib/rideTime';
import { coordinatorName } from '../../lib/coordinator';

const BATCH_RIDES = loader('../../data/queries/BatchRides.graphql');

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(2),
  },
  reviewContainer: {
    padding: theme.spacing(2),
    paddingBottom: theme.spacing(10),
  },
  timeLine: {
    lineHeight: '1.25',
  },
  locations: {
    marginTop: theme.spacing(2),
  },
  cardIcon: {
    marginRight: theme.spacing(1),
  },
  rideNotes: {
    marginTop: theme.spacing(3),
  },
  mncs: {
    marginTop: theme.spacing(3),
  },
  actionBar: {
    marginTop: theme.spacing(3),
  },
  fullHeight: {
    height: '100%',
  },
  coordinator: {
    marginRight: theme.spacing(0.5),
  },
  locationPhoto: {
    paddingLeft: 24,
  },
  feedbackSection: {
    marginTop: theme.spacing(2),
  },
  feedBackHeader: {
    display: 'flex',
    alignItems: 'center',
  },
  copyButton: {
    marginLeft: '0.5rem',
  },
  reasonForTransport: {
    marginTop: theme.spacing(2),
  },
  bestTimeInfoLink: {
    fontSize: 16,
    fontWeight: 500,
    textDecoration: 'underline',
    cursor: 'pointer',
    color: theme.palette.info.main,
    border: 'none',
    backgroundColor: 'transparent',
    padding: 0,
  },
}));

type RideDetailsViewProps = {
  ride: Ride;
  account: Account | null;
  admin: boolean;
  getRide?:
    | ((variables?: Partial<any>) => Promise<ApolloQueryResult<any>>)
    | undefined;
};

export const RideDetailsView: FC<RideDetailsViewProps> = ({
  ride,
  account,
  admin,
  getRide,
}) => {
  const isMetric = readIsMetric();
  const { webAppHost } = ENV;

  const classes = useStyles();
  const theme = useTheme();
  const [showReview, setShowReview] = useState(false);
  const [openBestTimeModal, setOpenBestTimeModal] = useState(false);
  const auth = useAuth();
  const { isEhrPatientContext } = useEhrContext();
  const search = useLocation().search;

  const [showFeedbackForm, setShowFeedbackForm] = useState(false);
  const [isFeedbackSent, setFeedbackSent] = useState(false);

  const [authToken, setAuthToken] = useState<OnwardToken | null>(null);

  const queryParams = new URLSearchParams(search);
  const shouldOpenReview = queryParams.get('openReview');
  const [getBatchRides, { data: batchRides }] = useLazyQuery(BATCH_RIDES, {
    fetchPolicy: 'network-only',
  });
  const { session } = useSessionContext();
  const bookRideFeatureFlag = session?.featureFlags?.includes(
    'dashboard_portal_book_ride',
  );
  const specializedTransportationNeedsFeatureFlag =
    session?.featureFlags?.includes('specialized_transport_needs');
  const encounterId =
    bookRideFeatureFlag && ride.ehrData?.encounterId
      ? ride.ehrData.encounterId
      : undefined;
  const buildRideUrl = (
    action: string,
    extraParams?: Record<string, string>,
  ) => {
    const params = new URLSearchParams();

    if (encounterId) {
      params.set('ehrEncounterId', encounterId);
    }

    if (extraParams) {
      for (const [k, v] of Object.entries(extraParams)) {
        params.set(k, v);
      }
    }

    const queryString = params.toString();
    return `/rides/${ride.id}/${action}${queryString ? `?${queryString}` : ''}`;
  };

  const rideAction = bookRideFeatureFlag ? 'rebook' : 'copy';
  const rebookRideUrl = buildRideUrl(rideAction);
  const returnTripUrl = buildRideUrl(rideAction, {
    [bookRideFeatureFlag ? 'returnTrip' : 'makeReturnTrip']: 'true',
  });
  const editRideUrl = buildRideUrl(bookRideFeatureFlag ? 'editRide' : 'edit');

  useEffect(() => {
    if (shouldOpenReview) {
      setShowReview(true);
    }
  }, [shouldOpenReview]);

  useEffect(() => {
    const token = getToken();
    setAuthToken(token);
  }, []);

  // we show bigger dates
  const largeScreen = useMediaQuery(theme.breakpoints.up('md'));

  // less padding
  const smallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const history = useHistory();
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);
  const [moveToRebook, setMoveToRebook] = useState(false);
  const [showMncs, setShowMncs] = useState(false);
  const [showMncsModal, setShowMncsModal] = useState(false);
  const [mncsModalMode, setMncsModalMode] = useState<'fill' | 'upload' | null>(
    null,
  );
  const [errorMessage, setErrorMessage] = useState('');

  // draft, pending, confirmed
  const [cancelDisabled, setCancelDisabled] = useState(
    ride.status !== 'draft' &&
      ride.status !== 'pending' &&
      ride.status !== 'confirmed',
  );

  const mncsAnswers = useMemo(() => {
    return ride?.medicalNecessityCertificationStatement?.mncsAnswers?.filter(
      answer => !!answer.value,
    );
  }, [ride]);

  const [declineBids, { data }] = useDeclineBestTimeBidsMutationMutation({
    onCompleted: () => {
      setOpenBestTimeModal(false);
      getRide?.();
    },
    onError: () => setErrorMessage('Something wet wrong'),
  });

  useEffect(() => {
    if (data?.declineBestTimeBids?.error) {
      setErrorMessage(data?.declineBestTimeBids?.error);
    }
  }, [data]);

  useEffect(() => {
    if (!!errorMessage) {
      setTimeout(() => {
        setErrorMessage('');
      }, 7000);
    }
  }, [errorMessage]);

  useEffect(() => {
    if (ride.batchId) {
      getBatchRides({ variables: { rideId: parseInt(ride.id) } });
    }
  }, [ride.batchId]);

  const hasPaymentMethod = !!(
    ride.paymentMethod || ride.payer?.currentPaymentMethod
  );

  const renderPaymentMethod = () => {
    if (!ride.paymentMethod) {
      if (account?.invoiced) {
        return <Typography variant="subtitle2">Invoiced</Typography>;
      } else {
        return null;
      }
    }

    return (
      <Box display="flex">
        <CreditCardIcon color="secondary" className={classes.cardIcon} />
        <Typography variant="subtitle2">{ride.paymentMethod.name}</Typography>
      </Box>
    );
  };

  const billingText = () => {
    return ride.rideType === 'delivery'
      ? 'Billing will begin when the driver begins shopping, and end when the driver drops off the delivery. 3% service fee will be applied only to delivery expenses.'
      : 'Billing will begin when the rider is picked up, and end when the rider is dropped off.';
  };

  const passengerAndDistanceText = () => {
    let text = '';
    if (ride.rideType !== 'delivery') {
      if (!ride.passengerCount || ride.passengerCount === 1) {
        text += '1 passenger';
      } else {
        text += `${ride.passengerCount} passengers`;
      }
      if (ride.estimatedDistanceMiles) {
        text += `, ${ride.estimatedDistanceMiles} `;
        ride.estimatedDistanceMiles > 1 ? (text += 'miles') : (text += 'mile');
      }
    }
    return text;
  };

  const RideNotesDetails = () => {
    if (ride.notes && ride.notes.length) {
      return <ParagraphText>{ride.notes}</ParagraphText>;
    }
    return <ZeroStateText>No details provided</ZeroStateText>;
  };

  const RideNotes = () => (
    <Box className={classes.rideNotes}>
      <Typography variant="button">
        {ride.rideType === 'delivery' ? 'Delivery Details' : 'Ride Notes'}
      </Typography>
      <RideNotesDetails />
    </Box>
  );

  const getWeight = (weight: number | null | undefined) => {
    if (!weight) {
      return null;
    }

    let text = '';
    if (weight) {
      if (isMetric) {
        text = `Weight: ${getKgFromLBS(weight)} kg`;
      } else {
        text = `Weight: ${ride.weightLbs} lbs`;
      }
    }

    return <ParagraphText>{text}</ParagraphText>;
  };

  const getHeight = (heightInches: number | null | undefined) => {
    if (!heightInches) {
      return null;
    }
    if (isMetric) {
      return (
        <ParagraphText>{`Height: ${getCmFromInches(
          heightInches,
        )} cm`}</ParagraphText>
      );
    }

    const heightInch = (heightInches || 0) % 12;
    const heightFeet = Math.trunc((heightInches || 0) / 12);

    if (heightInch && heightFeet) {
      return (
        <ParagraphText>{`Height: ${heightFeet} ft ${heightInch} in`}</ParagraphText>
      );
    }

    if (heightInch) {
      return <ParagraphText>{`Height: ${heightInch} in`}</ParagraphText>;
    }

    if (heightFeet) {
      return <ParagraphText>{`Height: ${heightFeet} ft`}</ParagraphText>;
    }

    return null;
  };

  const TransportTypeDetails = () => {
    return ride.bariatric ||
      ride.stairsCount ||
      ride.heightInches ||
      //@ts-ignore
      ride.wheelchairOwner ||
      ride.oxygenRequired ||
      ride.contactPrecautionsRequired ||
      ride.bedsideNurse ? (
      <Box className={classes.rideNotes}>
        <Typography variant="button">Transport Type Details</Typography>
        {ride.bariatric && (
          <ParagraphText>
            {specializedTransportationNeedsFeatureFlag
              ? 'Specialized Transport'
              : 'Bariatric'}
          </ParagraphText>
        )}
        {ride.stairsLabel && <ParagraphText>{ride.stairsLabel}</ParagraphText>}
        {getWeight(ride.weightLbs)}
        {getHeight(ride.heightInches)}
        {/*@ts-ignore*/}
        {ride.wheelchairOwner && (
          // @ts-ignore
          <ParagraphText>{`Wheelchair Owner: ${ride.wheelchairOwner}`}</ParagraphText>
        )}
        {ride.oxygenRequired && (
          <ParagraphText>Transport to Provide Oxygen</ParagraphText>
        )}
        {ride.contactPrecautionsRequired && (
          <ParagraphText>Droplet Precautions Required</ParagraphText>
        )}
        {ride.bedsideNurse?.fullName && (
          <ParagraphText>
            {`Nurse Name: ${ride.bedsideNurse.fullName}`}
          </ParagraphText>
        )}
        {ride.bedsideNurse?.phone && (
          <ParagraphText>{`Nurse Phone: ${ride.bedsideNurse.phone}`}</ParagraphText>
        )}
      </Box>
    ) : null;
  };

  const MncsAnswers = () => {
    if (ride.mncsMode === 'none' && !mncsAnswers) {
      return null;
    }

    const hasNewMncs = ride.mncsMode !== 'none';

    if (hasNewMncs) {
      const mncsStatement = ride?.medicalNecessityCertificationStatement;
      const downloadMncs = () => {
        if (!mncsStatement?.fileUrl || !authToken) {
          return;
        }

        fetch(mncsStatement.fileUrl, {
          method: 'GET',
          headers: {
            Accept: 'application/pdf',
            'Access-Token': authToken.accessToken,
            Client: authToken.client,
            Uid: authToken.uid,
          },
        })
          .then(response => response.blob())
          .then(blob => {
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = mncsStatement?.fileName || 'mncs.pdf';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
          })
          .catch(error => {
            console.error('Error downloading MNCS:', error);
            alert('Failed to download MNCS. Please try again.');
          });
      };

      const getMncsContentByMode = () => {
        if (ride.mncsMode === 'collected_at_pickup') {
          return (
            <Typography variant="body2">
              MNCS to be provided on pickup
            </Typography>
          );
        } else if (ride.mncsMode === 'marked_not_required') {
          return (
            <Typography variant="body2">MNCS form not required</Typography>
          );
        } else {
          return (
            <Link
              to=""
              onClick={e => {
                e.preventDefault();
                downloadMncs();
              }}
            >
              Download MNCS
            </Link>
          );
        }
      };

      // New MNCS, show either download link or MNCS mode
      return (
        <Box className={classes.mncs}>
          <Typography variant="button">
            Medical Necessity Certification Statement
          </Typography>
          <div
            style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}
          >
            {getMncsContentByMode()}
          </div>
        </Box>
      );
    }

    // Old MNCS, show the modal
    return (
      <Box className={classes.mncs}>
        <Typography variant="button">
          Medical Necessity Certification Statement
        </Typography>
        <div>
          <Link
            to=""
            onClick={e => {
              e.preventDefault();
              setShowMncs(true);
            }}
          >
            Show MNCS
          </Link>
        </div>
      </Box>
    );
  };

  const DeliverySubstitutions = () => {
    if (ride.rideType !== 'delivery') return null;

    const text =
      ride.deliverySubstitution === 'yes'
        ? 'Try to contact, and if not reachable make substitutions.'
        : 'Try to contact, and if not reachable do NOT make substitutions.';

    return (
      <Box className={classes.rideNotes}>
        <Box alignSelf="center">
          <Typography variant="button">Delivery Substitutions</Typography>
        </Box>

        <Typography variant="body2">{text}</Typography>
      </Box>
    );
  };

  const isFromRN = auth?.getFromRN(); // user login from react-native app using webview

  const RideActionButtons = () => {
    const isRiderDeactivated = ride.rider?.deactivatedAt;
    const rideStatusAllowsPortalEdit = [
      'draft',
      'pending',
      'confirmed',
    ].includes(ride.status);

    const isRideshare = ride.transportType === 'rideshare';

    const shouldShowEditForDashboardBook =
      !cancelDisabled && (admin || !ride.driver);
    const shouldShowEditForPortalBook =
      rideStatusAllowsPortalEdit && !isRideshare;
    const shouldShowEdit = bookRideFeatureFlag
      ? shouldShowEditForPortalBook
      : shouldShowEditForDashboardBook;
    const shouldShowEditPhoneNumber = bookRideFeatureFlag
      ? !rideStatusAllowsPortalEdit && ride.driver
      : !cancelDisabled && !admin && ride.driver;

    return (
      <Box
        data-testid="rideActions"
        display="flex"
        flexWrap="wrap"
        justifyContent="space-between"
        className={classes.actionBar}
      >
        <Box
          flexGrow={1}
          display="flex"
          flexWrap="wrap"
          alignItems="center"
          gridGap={8}
        >
          {shouldShowEdit && !isRideshare && (
            <Button
              data-testid="edit-btn"
              color="primary"
              variant="outlined"
              startIcon={<EditIcon />}
              component={Link}
              to={editRideUrl}
            >
              Edit
            </Button>
          )}
          {shouldShowEditPhoneNumber && (
            <Button color="primary" startIcon={<PhoneIcon />}>
              1 (800) 700-4797 to make changes
            </Button>
          )}
          {!cancelDisabled && (
            <Button
              color="primary"
              variant="outlined"
              startIcon={<CancelIcon />}
              onClick={() => {
                setMoveToRebook(false);
                setShowCancelConfirmation(true);
              }}
            >
              Cancel
            </Button>
          )}
          {!isRiderDeactivated && (
            <Button
              startIcon={<RedoIcon />}
              color="primary"
              variant="outlined"
              component={Link}
              to={rebookRideUrl}
            >
              Rebook
            </Button>
          )}
          {isFromRN &&
            ride.status === 'completed' &&
            !ride.custodianReviewedAt && (
              <Button
                startIcon={<RateReviewIcon />}
                color="primary"
                variant="outlined"
                onClick={() => {
                  setShowReview(true);
                  window.scrollTo({ top: 0 });
                }}
              >
                Rate Ride
              </Button>
            )}
          {!cancelDisabled && (
            <Button
              color="primary"
              variant="outlined"
              startIcon={<RedoIcon />}
              onClick={() => {
                setMoveToRebook(true);
                setShowCancelConfirmation(true);
              }}
            >
              Cancel and Rebook
            </Button>
          )}
          {!isRiderDeactivated && (
            <Button
              startIcon={<RedoIcon />}
              color="primary"
              variant="outlined"
              component={Link}
              to={returnTripUrl}
            >
              Return Trip
            </Button>
          )}
          <EhrWriteRideDetails ride={ride} />
        </Box>
      </Box>
    );
  };

  const FlexRideLabel = () => (
    <Typography
      className={classes.timeLine}
      variant="caption"
      style={{ fontStyle: 'italic' }}
    >
      Flexible Ride - Time TBD by Rider
    </Typography>
  );

  const renderRideInfo = () => {
    const {
      startLocation,
      startLocationPhoto,
      endLocation,
      endLocationPhoto,
      custodian,
      pickupRoomNumber,
      dropoffRoomNumber,
      bidRequestedStartTime,
    } = ride;
    const transportType = ride.custodian?.availableTransportTypes?.find(
      at => at.name === ride.transportType,
    );
    const { status, flexibleRide, biddingWindowEndsAt } = ride;

    let shouldShowBestTime =
      (!!biddingWindowEndsAt || !!bidRequestedStartTime) && !flexibleRide;
    const hideStartTime = flexibleRidePickupTimeTbd(ride);

    const ridePaymentMethod = ride?.paymentMethod;
    const isRiderDeactivated = ride.rider?.deactivatedAt;
    const hidePricingFromCustodian = ride.hidePricingFromCustodian;

    return (
      <>
        <Grid container spacing={3}>
          {status === 'inprogress' && (
            <Grid item xs={12}>
              <InProgressIndicator />
            </Grid>
          )}

          <Grid item xs={12}>
            {!!errorMessage ? (
              <Alert severity="error">{errorMessage}</Alert>
            ) : null}
          </Grid>

          <Grid item xs={12} sm={6}>
            <Box
              className={classes.fullHeight}
              display="flex"
              flexDirection="column"
              justifyContent="space-between"
            >
              <Box>
                <Typography variant="h6">{personName(ride.rider)}</Typography>

                {isRiderDeactivated && (
                  <DeactivatedLabel style={{ margin: 4 }} />
                )}

                <Box display="flex" alignItems="center">
                  <Box>
                    <Typography
                      className={classes.timeLine}
                      variant="subtitle1"
                    >
                      {largeScreen
                        ? longDateOnly(ride.requestedStartTime, {
                            timeZone: account?.tzName,
                          })
                        : mediumDateOnly(ride.requestedStartTime, {
                            timeZone: account?.tzName,
                          })}
                    </Typography>
                    {hideStartTime ? (
                      <FlexRideLabel />
                    ) : (
                      <Typography
                        className={classes.timeLine}
                        variant="subtitle1"
                      >
                        <Typography
                          component="span"
                          variant="body2"
                          className={classes.coordinator}
                        >
                          Requested Start Time:{' '}
                        </Typography>
                        {timeRange(
                          ride.requestedStartTime,
                          ride.requestedEndTime,
                          {
                            timeZone: account?.tzName,
                          },
                        )}
                      </Typography>
                    )}
                    {shouldShowBestTime ? (
                      <button
                        className={classes.bestTimeInfoLink}
                        onClick={() => setOpenBestTimeModal(true)}
                      >
                        Best Time Info
                      </button>
                    ) : null}
                  </Box>
                </Box>
              </Box>
              <Box mt={2}>
                <RideEtaDisplay ride={ride} account={account} inline />
              </Box>
              <Box mt={2} display="flex" alignItems="center">
                <Typography variant="button" className={classes.coordinator}>
                  Coordinator:
                </Typography>
                <Typography variant="body2">
                  {coordinatorName(
                    custodian,
                    session?.account?.id,
                    session?.account?.featureFlags,
                  )}
                </Typography>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box display="flex" alignItems="end">
              <RideTypeBadge rideType={ride.rideType} />
              <Box ml={1}>
                <TransportTypeBadge transportType={transportType} />
              </Box>
              {ride.batchId && (
                <Box ml={1}>
                  <RideTypeBadge rideType={'recurring'} />
                </Box>
              )}
            </Box>
            <Box display="flex" className={classes.locations}>
              <Box flexGrow={1}>
                <LocationRow
                  location={startLocation}
                  roomNumber={pickupRoomNumber}
                  roomNumberTitle="Pickup Room Number"
                  locationPhoto={startLocationPhoto}
                  showAddress
                  showInstructions
                />
                <LocationRow
                  location={endLocation}
                  roomNumber={dropoffRoomNumber}
                  roomNumberTitle="Dropoff Room Number"
                  locationPhoto={endLocationPhoto}
                  showAddress
                  showInstructions
                />
              </Box>
            </Box>
          </Grid>
        </Grid>

        <Map
          // @ts-ignore
          startLocation={startLocation}
          endLocation={endLocation}
          startTime={!hideStartTime && ride.requestedStartTime}
          endTime={ride.requestedEndTime}
          tzName={account?.tzName}
          height="250px"
        />
        <Box display="flex" className={classes.locations}>
          <Box flexGrow={1}>
            {ride.estimatedCostCents != null && (
              <Typography variant="subtitle2">{`Total Fare Estimate: ${centsToFormattedDollars(
                ride.estimatedCostCents,
              )}`}</Typography>
            )}
            {ride.splitPayerEstimates ? (
              <>
                <SplitPaymentSummary
                  paymentMethodOptions={
                    ridePaymentMethod ? [ridePaymentMethod] : []
                  }
                  splitPayerEstimates={ride.splitPayerEstimates}
                  invoiced={account?.invoiced}
                  paymentMethodLink={renderPaymentMethodLink(
                    ride.ridePaymentUrl,
                    hasPaymentMethod,
                    false,
                  )}
                  hideFacilityPricing={hidePricingFromCustodian}
                  detailView
                />
              </>
            ) : (
              renderPaymentMethod()
            )}
          </Box>
          <Box>
            <Typography variant="subtitle2">
              {passengerAndDistanceText()}
            </Typography>
          </Box>
        </Box>
        <Box>
          <Typography color="textSecondary" variant="caption">
            {billingText()}
          </Typography>
        </Box>
        <TransportTypeDetails />
        <MncsAnswers />
        <RideNotes />
        {!ride.splitPayerEstimates && <AddRidePaymentMethodLink />}
        <FeedbackView />
        <DeliverySubstitutions />

        <ReasonForTransport />
        <RideActionButtons />
      </>
    );
  };

  const renderPaymentMethodLink = (
    ridePaymentUrl?: Maybe<string | undefined>,
    hasCurrentPaymentMethod = false,
    displayCopyButton = true,
  ) => {
    if (!ridePaymentUrl) {
      return null;
    }

    return (
      <>
        <Grid item>
          <a href={ridePaymentUrl} target="_blank" rel="noopener noreferrer">
            {hasCurrentPaymentMethod
              ? 'Change Payment Method'
              : 'Add Payment Method'}
          </a>
          {displayCopyButton && (
            <Button
              startIcon={<FileCopyIcon />}
              variant="outlined"
              className={classes.copyButton}
              onClick={() => navigator.clipboard.writeText(ridePaymentUrl)}
            >
              Copy URL
            </Button>
          )}
        </Grid>
      </>
    );
  };

  const AddRidePaymentMethodLink = () => {
    let content = null;

    content = renderPaymentMethodLink(ride.ridePaymentUrl, hasPaymentMethod);

    if (!content) {
      return null;
    }

    return (
      <Grid container className={classes.feedbackSection}>
        <Grid className={classes.feedBackHeader} item xs={12}>
          <Typography variant="button">Private Pay Ride</Typography>
        </Grid>
        {content}
      </Grid>
    );
  };

  const FeedbackView = () => {
    const { rideFeedback, rideFeedbackRequestPath } = ride;

    let content = null;

    let feedbackContent = null;

    if (!rideFeedback && !rideFeedbackRequestPath) {
      content = <Typography variant="body2">No feedback available</Typography>;
    }

    if (!rideFeedback && rideFeedbackRequestPath) {
      const link = `${webAppHost}${rideFeedbackRequestPath}`;
      content = (
        <>
          <Grid item>
            <Link
              to={rideFeedbackRequestPath}
              target="_blank"
              rel="noopener noreferrer"
            >
              Feedback Page
            </Link>
            <Button
              startIcon={<FileCopyIcon />}
              variant="outlined"
              className={classes.copyButton}
              onClick={() => navigator.clipboard.writeText(link)}
            >
              Copy URL
            </Button>
          </Grid>
          {!admin ? null : (
            <Grid item>
              <Button
                startIcon={<FlashOnOutlinedIcon />}
                className={classes.copyButton}
                disabled={isFeedbackSent}
                onClick={() => setShowFeedbackForm(true)}
                variant="outlined"
              >
                Send Feedback Request
              </Button>
            </Grid>
          )}
        </>
      );
    }

    if (rideFeedback) {
      const { driverFeedback, createdAt, stars } = rideFeedback;

      feedbackContent = (
        <Grid container>
          <Grid xs={12} item>
            <Typography>
              {mediumDateOnly(createdAt, { timeZone: account?.tzName })}
            </Typography>
          </Grid>
          <Grid xs={12} item>
            <Rating
              disabled
              name="hover-feedback"
              value={stars}
              size="large"
              precision={1}
            />
          </Grid>
          <Grid xs={12} item>
            <Typography>{driverFeedback}</Typography>
          </Grid>
        </Grid>
      );
    }

    return (
      <Grid container className={classes.feedbackSection}>
        <Grid className={classes.feedBackHeader} item xs={12}>
          <Typography variant="button">Rider Feedback</Typography>
        </Grid>
        {content}
        {feedbackContent}
      </Grid>
    );
  };

  if (showMncs && mncsAnswers) {
    return (
      <AddMedicalNecessityStatementView
        onGoBack={() => {
          setShowMncs(false);
        }}
        mncsAnswers={mncsAnswers}
      />
    );
  }

  const handleCancelRide = () => {
    if (moveToRebook) {
      history.push(rebookRideUrl);
    } else if (isEhrPatientContext) {
      // If in an EHR patient context, always go back to rider profile page.
      history.push(`/riders/${ride.rider?.id}?tab=rides`, {
        from: 'ehr',
      });
    } else {
      history.replace({ pathname: '/' });
    }
  };

  // This is to check if the ride was created from the iframed Portal. It does
  // not have the history location key, this means it should be redirected to
  // upcoming page. If the user came from the history page, the key should
  // exist, which will use the router history to navigate.
  const isHistoryRide = history.location.key === undefined;

  const handleGoBack = () => {
    if (isEhrPatientContext) {
      // If in an EHR patient context, always go back to rider profile page.
      history.push(`/riders/${ride.rider?.id}?tab=rides`, {
        from: 'ehr',
      });
    } else if (bookRideFeatureFlag && isHistoryRide) {
      history.replace({ pathname: '/' });
    } else {
      history.goBack();
    }
  };

  const ReasonForTransport = () => {
    const { transportReason, transportReasonComment } = ride;

    if (!transportReason) {
      return null;
    }

    return (
      <Grid container className={classes.reasonForTransport}>
        <Grid className={classes.feedBackHeader} item xs={12}>
          <Typography variant="button">Reason For Transport</Typography>
        </Grid>
        <Typography variant="body2">
          <p>
            Category: <b>{transportReason.categoryName}</b>
          </p>
          <p>
            Reason: <b>{transportReason.name}</b>
          </p>
          {transportReasonComment ? (
            <p>
              Comment: <b>{transportReasonComment}</b>
            </p>
          ) : null}
        </Typography>
      </Grid>
    );
  };

  return (
    <Container maxWidth="md" disableGutters={smallScreen}>
      <Button
        color="primary"
        startIcon={<ArrowBackIcon />}
        onClick={showReview ? () => setShowReview(false) : handleGoBack}
      >
        Back
      </Button>

      {isFromRN && ride && showReview ? (
        <Paper className={classes.reviewContainer}>
          <Review
            allowTipping
            closeReview={() => setShowReview(false)}
            // @ts-ignore
            ride={ride}
          />
        </Paper>
      ) : (
        <>
          <Paper className={classes.root}>{renderRideInfo()}</Paper>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <DriverCard ride={ride} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <RiderCard ride={ride} />
            </Grid>
          </Grid>
        </>
      )}
      <CancelConfirmation
        ride={ride}
        handleClose={() => setShowCancelConfirmation(false)}
        handleCancelRide={handleCancelRide}
        showDialog={showCancelConfirmation}
        cancelDisabled={cancelDisabled}
        setCancelDisabled={setCancelDisabled}
        isBatch={!!ride.batchId}
        batchRemainingRides={batchRides?.batchRides}
        isRebook={moveToRebook}
      />

      <FeedbackForm
        email={ride.rider?.email}
        phone={ride.rider?.phone}
        showDialog={showFeedbackForm}
        handleClose={() => setShowFeedbackForm(false)}
        setFeedbackSent={sent => {
          setFeedbackSent(sent);
        }}
        rideId={ride.id}
      />

      <BestTimeDialog
        setOpenBestTimeModal={setOpenBestTimeModal}
        openBestTimeModal={openBestTimeModal}
        handleDeclineBestTime={() => {
          declineBids({ variables: { rideId: ride.id } });
        }}
        ride={ride}
      />

      <MncsModal
        isOpen={showMncsModal}
        onClose={() => {
          setShowMncsModal(false);
          setMncsModalMode(null);
        }}
        rideId={ride.id}
        mode={mncsModalMode}
        mncsId={ride.medicalNecessityCertificationStatement?.id}
      />
    </Container>
  );
};
